import ApiNgUtil from "../api/ApiNgUtil";
import { convertListResponse, ListResponse } from "../api/ApiNgModels";
import { formatToCurrency } from "../common/helpers/CurrencyHelpers";
import { Agent, Player, AgentOverviewFilter, AgentOverviewItem, AgentPlayerOverviewItem, SyndicateOverviewItem, PlayerId, AgentUserOverviewItem, AgentUser, AgentUserUpdate, SyndicateShare, SyndicateShareItem } from "./AgentModels";
import queryString from "query-string";
import { GenericResponse } from "../CreateRound/CreateRoundModels";
import { getListWithId, getObject } from "../api/dataProvider";
import CreateRoundUtil from "../CreateRound/CreateRoundUtil";
import InvokeApiMethods from "../ExternalApi/ExternalApiModels";
import fetchApi from "../../fetchApi";

class AgentUtil {
    static SYNDICATE_STATUS_DRAFT = "DRAFT";
    static SYNDICATE_STATUS_SUBMITTED = "SUBMITTED";
    static SYNDICATE_STATUS_PAUSED = "PAUSED";

    static getUrlParams(pageSize: number, from: Date | undefined, to: Date | undefined, lastId: any, filter?: any): string {
        var urlParams = `limit=${pageSize}&lastId=${lastId}`;
        if (from) {
            urlParams += `&from=${from.toISOString()}`
        }
        if (to) {
            urlParams += `&to=${to.toISOString()}`
        }
        if (filter) {
            const query = Object.assign({
                filter: JSON.stringify(filter)
            });
            urlParams += `&${queryString.stringify(query)}`;
        }
        return urlParams;
    }
    static async getAgentOverviews(pageSize: number, from: Date| undefined, to: Date| undefined, lastId: number, filter?: AgentOverviewFilter) : Promise<GenericResponse<ListResponse<AgentOverviewItem>>> {
        var urlParams = AgentUtil.getUrlParams(pageSize, from, to, lastId, filter);
        var url = "agents/overviews?" + urlParams;
        var listResponse = await ApiNgUtil.getListByUrl<AgentOverviewItem>(url, "agentOverviews");
        AgentUtil.fixAgentOverviewItems(listResponse.item?.items);
        return listResponse;
    }
    private static fixAgentOverviewItems(items?: AgentOverviewItem[]) {
        if (!items) {
            return;
        }
        for (var item of items) {
            if (item.profileText !== undefined && item.profileText !== null) {
                item.profileTextSize = item.profileText.length;
            }
        }
    }
    static async getSyndicateOverviews(agentId: number, pageSize: number, from: Date| undefined, to: Date| undefined, lastId: number) : Promise<GenericResponse<ListResponse<SyndicateOverviewItem>>> {
        var urlParams = AgentUtil.getUrlParams(pageSize, from, to, lastId, undefined);
        var url = `agents/${agentId}/syndicateoverviews?${urlParams}`;
        var listResponse = await ApiNgUtil.getListByUrl<SyndicateOverviewItem>(url, "syndicateOverviews");
        if (listResponse.item?.items) {
            AgentUtil.fixSyndicateOverviews(listResponse.item.items)
        }
        return listResponse;
    }
    static async getAgentPlayerOverviews(agentId: number, pageSize: number, from: Date| undefined, to: Date| undefined, lastId: number) : Promise<GenericResponse<ListResponse<AgentPlayerOverviewItem>>> {
        var urlParams = AgentUtil.getUrlParams(pageSize, from, to, lastId, undefined);
        var url = `agents/${agentId}/playeroverviews?${urlParams}`;
        var listResponse = await ApiNgUtil.getListByUrl<AgentPlayerOverviewItem>(url, "agentPlayerOverviews");
        return listResponse;
    }
    static async getAgentUserOverviews(agentId: number, pageSize: number, from: Date| undefined, to: Date| undefined, lastId: number) : Promise<GenericResponse<ListResponse<AgentUserOverviewItem>>> {
        var urlParams = AgentUtil.getUrlParams(pageSize, from, to, lastId, undefined);
        var url = `agents/${agentId}/userOverviews?${urlParams}`;
        var listResponse = await ApiNgUtil.getListByUrl<AgentUserOverviewItem>(url, "agentUserOverviews");
        return listResponse;
    }
    static async getSyndicateShares(syndicateId: number, pageSize: number, lastId?: number) : Promise<GenericResponse<ListResponse<SyndicateShare>>> {
        var urlParams = AgentUtil.getUrlParams(pageSize, undefined, undefined, lastId, undefined);
        var url = `syndicates/${syndicateId}/shares?${urlParams}`;
        var listResponse = await ApiNgUtil.getListByUrl<SyndicateShare>(url, "syndicateShares");
        return listResponse;
    }
    static async getSyndicateShareItems(syndicateId: number, pageSize: number, lastId?: number) : Promise<GenericResponse<ListResponse<SyndicateShareItem>>> {
        var urlParams = AgentUtil.getUrlParams(pageSize, undefined, undefined, lastId, undefined);
        var url = `syndicates/${syndicateId}/shares?${urlParams}`;
        var innerResponse = await AgentUtil.getSyndicateShares(syndicateId, pageSize, lastId);
        var response : GenericResponse<ListResponse<SyndicateShareItem>> = { success: innerResponse.success, message: innerResponse.message, item: null};
        if (!innerResponse.item) {
            return response;
        }
        const innerItem : ListResponse<SyndicateShare> = innerResponse.item;
        response.item = convertListResponse<SyndicateShare, SyndicateShareItem>(innerItem);
        if (innerItem.items) {
            response.item.items = AgentUtil.toSyndicateShareItems(innerItem.items);
        }
        return response;
    }
    static toSyndicateShareItems(shares: SyndicateShare[]): SyndicateShareItem[] {
        var items : SyndicateShareItem[] = [];
        for (const share of shares) {
            var item : SyndicateShareItem = {
                id: share.id,
                status: share.status,
                playerId: share.playerId,
                couponShareId: share.couponShareId,
                syndicateId: share.syndicateId,
                reservationId: share.reservationId,
            };
            const cs = share.couponShare;
            if (cs) {
                item.couponShareStatus = cs.couponShareStatus;
                item.roundId = cs.roundId;
                item.roundTypeId = cs.roundTypeId;
                item.roundTypeName = cs.roundTypeName;
                item.deadline = cs.deadline;
                item.operatorId = cs.operatorId;
                item.agentId = cs.agentId;
                item.agentName = cs.agentName;
                item.couponId = cs.couponId;
                item.placedAt = cs.placedAt;
                item.currency = cs.currency;
                item.stake = cs.stake;
                item.result = cs.result;
                item.refund = cs.refund;
                item.fee = cs.fee;
                item.unitCurrency = cs.unitCurrency;
                item.unitStake = cs.unitStake;
                item.unitResult = cs.unitResult;
                item.unitRefund = cs.unitRefund;
                item.unitFee = cs.unitFee;
                item.agentFee = cs.agentFee;
                item.unitAgentFee = cs.unitAgentFee;
                item.freebetId = cs.freebetId;
            }
            items.push(item);
        }
        return items;
    }

    static compareAgentOverviewItemByOperatorAgent(x: AgentOverviewItem, y: AgentOverviewItem) : number {
        var comp = x.operatorId.localeCompare(y.operatorId);
        if (comp !== 0) {
            return comp;
        }
        return x.name.localeCompare(y.name);
    }
    static async getAgent(id: number) : Promise<GenericResponse<Agent>> {
        const METHOD_NAME = "AgentUtil.getAgent";
        try {
            var url = `agents/${id}`;
            var resp = await ApiNgUtil.getObject<Agent>(url);
            return resp;
        } catch (error) {
            console.error(METHOD_NAME + " error.", error);
            return CreateRoundUtil.createGenericResponse<Agent>(false, METHOD_NAME + " error: " + error, null);
        }
     }
     static async getSyndicate(id: number) : Promise<GenericResponse<SyndicateOverviewItem>> {
        const METHOD_NAME = "AgentUtil.getSyndicate";
        try {
            var url = `syndicates/${id}`;
            var resp = await ApiNgUtil.getObject<SyndicateOverviewItem>(url);
            AgentUtil.fixSyndicateOverview(resp.item)
            return resp;
        } catch (error) {
            console.error(METHOD_NAME + " error.", error);
            return CreateRoundUtil.createGenericResponse<SyndicateOverviewItem>(false, METHOD_NAME + " error: " + error, null);
        }
     }
     static getSyndicateHiddenText(nSharesHidden: boolean, couponHidden: boolean) : string {
        var hiddenText = "";
        if (nSharesHidden) {
            hiddenText = "Sold";
        }
        if (couponHidden) {
            if (hiddenText.length > 0) {
                hiddenText += "/Coupon";
            } else {
                hiddenText = "Coupon";
            }
        }
        return hiddenText;
    }
    static getSyndicateOverviewHiddenText(syndicate: SyndicateOverviewItem) : string {
        var hiddenText = "";
        if (syndicate.nSharesHidden) {
            hiddenText = "Sold";
        }
        if (syndicate.couponHidden) {
            if (hiddenText.length > 0) {
                hiddenText += "/Coupon";
            } else {
                hiddenText = "Coupon";
            }
        }
        return hiddenText;
    }
    static fixSyndicateOverview(syndicate: SyndicateOverviewItem | null) {
        if (syndicate) {
            syndicate.hiddenText = AgentUtil.getSyndicateOverviewHiddenText(syndicate);
        }
    }
    static fixSyndicateOverviews(syndicates: SyndicateOverviewItem[] | null) {
        if (!syndicates) {
            return;
        }
        for (var syndicate of syndicates) {
            AgentUtil.fixSyndicateOverview(syndicate);
        }
    }
    static async getPlayersWithId(ids: number[]) : Promise<GenericResponse<Player[]>> {
        const METHOD_NAME = "CreateRoundUtil.getPlayersWithId";
        try {
            // var resp = await getListWithId("players", ids);
            // var players: Player[] = resp.data;
            // return CreateRoundUtil.createGenericResponse<Player[]>(true, "", players);
            //It doesn't work get list of players, so get them 1 by 1.
            var players: Player[] = [];
            for (var id of ids) {
                var resp = await AgentUtil.getPlayer(id);
                if (!resp.success) {
                    return CreateRoundUtil.createGenericResponse<Player[]>(false, `GetPlayer error [Id:${id}]: ${resp.message}`, players);
                }
                if (resp.item) {
                    players.push(resp.item);
                }
            }
            return CreateRoundUtil.createGenericResponse<Player[]>(true, "", players);
        } catch (error) {
            console.error(METHOD_NAME + " error.", error);
            return CreateRoundUtil.createGenericResponse<Player[]>(false, METHOD_NAME + " error: " + error, null);
        }
    }
    static async getPlayer(id: number) : Promise<GenericResponse<Player>> {
        const METHOD_NAME = "CreateRoundUtil.getPlayer";
        try {
            var resourceUrl = `players/${id}`;
            var resp = await getObject(resourceUrl);
            var player: Player = resp.data;
            if (!player) {
                return CreateRoundUtil.createGenericResponse<Player>(false, "Player not found", null);
            }
            return CreateRoundUtil.createGenericResponse<Player>(true, "", player);
        } catch (error) {
            console.error(METHOD_NAME + " error.", error);
            return CreateRoundUtil.createGenericResponse<Player>(false, METHOD_NAME + " error: " + error, null);
        }
    }
    static async saveAgent(id: number, saveJson: string) : Promise<GenericResponse<Agent>> {
        const METHOD_NAME = "CreateRoundUtil.saveAgent";
        var url = "agents";
        var method = InvokeApiMethods.POST();
        if (id && id > 0) {
            url += `/${id}`;
            method = InvokeApiMethods.PATCH();
        }
        try {
            var resp = await ApiNgUtil.getObjectWithBody<Agent>(url, method, saveJson);
            return resp;
        } catch (error) {
            console.error(METHOD_NAME + " error.", error);
            return CreateRoundUtil.createGenericResponse<Agent>(false, METHOD_NAME + " error: " + error, null);
        }
    }
    static async updateSyndicate(id: number, saveJson: string) : Promise<GenericResponse<SyndicateOverviewItem>> {
        const METHOD_NAME = "CreateRoundUtil.updateSyndicate";
        if (!id || id <= 0) {
            return CreateRoundUtil.createGenericResponse<SyndicateOverviewItem>(false, "Id not specified", null);
        }
        var url = `syndicates/${id}`;
        var method = InvokeApiMethods.PATCH();
        try {
            var resp = await ApiNgUtil.getObjectWithBody<SyndicateOverviewItem>(url, method, saveJson);
            return resp;
        } catch (error) {
            console.error(METHOD_NAME + " error.", error);
            return CreateRoundUtil.createGenericResponse<SyndicateOverviewItem>(false, METHOD_NAME + " error: " + error, null);
        }
    }
    static async getAgentUsers(agentId: number, pageSize: number, lastId: number) : Promise<GenericResponse<ListResponse<AgentUser>>> {
        var urlParams = AgentUtil.getUrlParams(pageSize, undefined, undefined, lastId, undefined);
        var url = `agents/${agentId}/users?${urlParams}`;
        var listResponse = await ApiNgUtil.getListByUrl<AgentUser>(url, "agentUsers");
        return listResponse;
     }
    static async getAgentUser(id: number) : Promise<GenericResponse<AgentUser>> {
        const METHOD_NAME = "AgentUtil.getAgentUser";
        try {
            var url = `agents/users/${id}`;
            var resp = await ApiNgUtil.getObject<AgentUser>(url);
            return resp;
        } catch (error) {
            console.error(METHOD_NAME + " error.", error);
            return CreateRoundUtil.createGenericResponse<AgentUser>(false, METHOD_NAME + " error: " + error, null);
        }
     }
    static async saveAgentUser(id: number, agentId: number, update: AgentUserUpdate) : Promise<GenericResponse<AgentUser>> {
        const METHOD_NAME = "CreateRoundUtil.saveAgentUser";
        var url = `agents/${agentId}/users`;
        var method = InvokeApiMethods.POST();
        if (id && id > 0) {
            url = `agents/users/${id}`;
            method = InvokeApiMethods.PATCH();
        }
        try {
            var saveJson = JSON.stringify(update);
            var resp = await ApiNgUtil.getObjectWithBody<AgentUser>(url, method, saveJson);
            return resp;
        } catch (error) {
            console.error(METHOD_NAME + " error.", error);
            return CreateRoundUtil.createGenericResponse<AgentUser>(false, METHOD_NAME + " error: " + error, null);
        }
    }
}
export default AgentUtil;